import { Box, CircularProgress, Divider, Stack } from '@mui/material';
import BackendService from 'BackendService';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { ActivityLog as ActivityLogType } from '../../models/ActivityLog';
import { Article } from '../../models/Article';
import FundOpportunity from '../../models/FundOpportunity';
import { getModelIcon, getModelLabel } from '../../utils/ModelIconMapping';
import { Palette } from 'Palette';
import { StyledCard, StyledCardContent, StyledTypography } from '../styles/styles';
import ActivityLogModelCard from './ActivityLogModelCard';
import { getActivityLogDescriptionDisplayName } from './contentVersionViewerUtils';

interface ContentVersionModelInfoCardProps {
  activityLog: ActivityLogType;
  tone: keyof typeof Palette;
}

interface ActivityLogWithDescriptions {
  initiatedByDescription?: {
    name?: string;
    contactName?: string;
    fundOpportunityName?: string;
    entityName?: string;
  };
}

const ContentVersionModelInfoCard: React.FC<ContentVersionModelInfoCardProps> = ({
  activityLog,
  tone,
}) => {
  const [modelData, setModelData] = useState<FundOpportunity | Article | null>(null);
  const [activityLogWithDescriptions, setActivityLogWithDescriptions] =
    useState<ActivityLogWithDescriptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadModelData = async () => {
      try {
        setLoading(true);
        setError(null);

        const backendService = new BackendService();
        const modelId = activityLog.associatedModelExternalId;

        if (activityLog.associatedModelType === 'FUND_OPPORTUNITY') {
          const response = await backendService.getFundOpportunity(modelId);
          setModelData(response.data);
        } else if (activityLog.associatedModelType === 'ARTICLE') {
          const article = await backendService.getArticle(modelId);
          setModelData(article);
        }

        try {
          const activityLogResponse = await backendService.getActivityLogEntry(activityLog.id);
          setActivityLogWithDescriptions(activityLogResponse as ActivityLogWithDescriptions);
        } catch (err) {
          console.warn('Could not load activity log descriptions:', err);
        }
      } catch (err: unknown) {
        console.error('Error loading model data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load model data');
      } finally {
        setLoading(false);
      }
    };

    loadModelData();
  }, [activityLog]);

  if (loading) {
    return (
      <StyledCard tone={tone} sx={{ mb: 3 }}>
        <StyledCardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        </StyledCardContent>
      </StyledCard>
    );
  }

  if (error || !modelData) {
    return (
      <StyledCard tone={tone} sx={{ mb: 3 }}>
        <StyledCardContent>
          <StyledTypography color="text.secondary">
            Could not load model information
          </StyledTypography>
        </StyledCardContent>
      </StyledCard>
    );
  }

  const isFundOpportunity = activityLog.associatedModelType === 'FUND_OPPORTUNITY';
  const fundOpportunity = isFundOpportunity ? (modelData as FundOpportunity) : null;
  const article = !isFundOpportunity ? (modelData as Article) : null;

  return (
    <StyledCard tone={tone} sx={{ mb: 3 }}>
      <StyledCardContent>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <StyledTypography
                tone={tone}
                fontSize="0.875rem"
                sx={{ mr: 1, minWidth: '60px', opacity: 0.7 }}
              >
                <strong>Model:</strong>
              </StyledTypography>
              <ActivityLogModelCard
                title={
                  isFundOpportunity
                    ? fundOpportunity?.name || 'Unnamed Opportunity'
                    : article?.title || 'Untitled Article'
                }
                subtitle={getModelLabel(activityLog.associatedModelType)}
                icon={getModelIcon(activityLog.associatedModelType)}
                tone={tone}
                modelType={activityLog.associatedModelType}
                modelId={activityLog.associatedModelExternalId}
              />
            </Box>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <StyledTypography
                tone={tone}
                fontSize="0.875rem"
                sx={{ mr: 1, minWidth: '60px', opacity: 0.7 }}
              >
                <strong>Initiator:</strong>
              </StyledTypography>
              <ActivityLogModelCard
                title={getActivityLogDescriptionDisplayName(
                  activityLogWithDescriptions?.initiatedByDescription,
                )}
                subtitle={getModelLabel(activityLog.initiatedByType)}
                icon={getModelIcon(activityLog.initiatedByType)}
                tone={tone}
                modelType={activityLog.initiatedByType}
                modelId={activityLog.initiatedByExternalId}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Stack spacing={1}>
          {isFundOpportunity && fundOpportunity && (
            <>
              {fundOpportunity.addresses && fundOpportunity.addresses.length > 0 && (
                <Box>
                  <StyledTypography variant="body2" fontWeight={600}>
                    Location:
                  </StyledTypography>
                  <StyledTypography variant="body2">
                    {fundOpportunity.addresses[0].fullAddress || 'Not specified'}
                  </StyledTypography>
                </Box>
              )}

              {fundOpportunity.fundSize && (
                <Box>
                  <StyledTypography variant="body2" fontWeight={600}>
                    Fund Size:
                  </StyledTypography>
                  <StyledTypography variant="body2">
                    ${(fundOpportunity.fundSize / 1000000).toFixed(1)}M
                  </StyledTypography>
                </Box>
              )}

              <Box>
                <StyledTypography variant="body2" fontWeight={600}>
                  Status:
                </StyledTypography>
                <StyledTypography variant="body2">{fundOpportunity.status}</StyledTypography>
              </Box>
            </>
          )}

          {!isFundOpportunity && article && (
            <>
              <Box>
                <StyledTypography variant="body2" fontWeight={600}>
                  Status:
                </StyledTypography>
                <StyledTypography variant="body2">{article.status}</StyledTypography>
              </Box>

              {article.createdAt && (
                <Box>
                  <StyledTypography variant="body2" fontWeight={600}>
                    Created:
                  </StyledTypography>
                  <StyledTypography variant="body2">
                    {format(new Date(article.createdAt), 'PPP')}
                  </StyledTypography>
                </Box>
              )}
            </>
          )}
        </Stack>
      </StyledCardContent>
    </StyledCard>
  );
};

export default ContentVersionModelInfoCard;
